import {
  Center,
  Icon,
  Image,
  VStack,
  forwardRef,
  motion,
} from '@hover/blueprint';
import { iCheck } from '@hover/icons';
import React from 'react';

import { MasonryItem } from 'src/modules/homeownerApp/components/Masonry';
import {
  formatSecondaryTextForDisplayImage,
  formatSpaceType,
} from 'src/modules/homeownerApp/components/Utils';
import { useFormattedTimeAgo } from 'src/modules/homeownerApp/hooks';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { BottomText } from './BottomText';

interface FavoriteItemProps {
  isSelected: boolean;
  onClick: () => void;
  displayImage: AiDisplayImage;
}

const MotionCenter = motion(Center);

const SavedItem = forwardRef<FavoriteItemProps, 'div'>(
  (
    {
      isSelected,
      onClick,
      displayImage,
    }: {
      isSelected: boolean;
      onClick: () => void;
      displayImage: AiDisplayImage;
    },
    ref,
  ) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
    const { id, aiGeneratedImage, spaceType, createdAt } = displayImage;
    const { generatedImagePath, imagePath } = aiGeneratedImage;
    const imageSrc = generatedImagePath || imagePath;
    const dateCreated = useFormattedTimeAgo(createdAt);

    const secondaryText = formatSecondaryTextForDisplayImage(
      t('generatedImageThumbnail.customized'),
      dateCreated,
      displayImage.aiGeneratedImage,
    );

    return (
      <MasonryItem ref={ref}>
        <VStack alignItems="flex-start">
          <Center key={id} marginBottom={200} position="relative">
            <Image
              alt={`saved ${id} - ${secondaryText}`}
              borderRadius={300}
              cursor="pointer"
              filter={isSelected ? 'brightness(0.6)' : 'none'}
              height="100%"
              objectFit="cover"
              onClick={onClick}
              src={imageSrc}
              transition="filter 0.2s ease-in-out"
              width="100%"
            />
            {isSelected && (
              <MotionCenter
                animate={{ opacity: 1 }}
                backgroundColor="neutral.0"
                borderRadius="round"
                exit={{ opacity: 0 }}
                height={100}
                initial={{ opacity: 0 }}
                position="absolute"
                width={100}
              >
                <Icon icon={iCheck} />
              </MotionCenter>
            )}
          </Center>
          <BottomText
            primaryText={formatSpaceType(spaceType as string)}
            secondaryText={secondaryText}
          />
        </VStack>
      </MasonryItem>
    );
  },
);

export { SavedItem };
